import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Divider, Tab, Tabs, Typography } from '@mui/material';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../loading-page';
import { partnerManagedTooltip } from '../../../shared/utils/partner-managed-utils';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { ISurveyQuestion, ISurveyQuestionGroup, ISurveyQuestionType } from '../../../shared/services/survey/survey.model';
import { AddGroupItemDialog } from '../../dialogs/surveys/add-group-item-dialog';
import { SurveyQuestionDialog } from '../../dialogs/surveys/survey-question-dialog';
import { SurveyQuestionGroupDialog } from '../../dialogs/surveys/survey-question-group-dialog';
import { SurveyQuestionsPanel } from '../../presentation-components/surveys/builder/survey-questions-panel';
import { SurveyCalculationsPanel } from '../../presentation-components/surveys/builder/survey-calculations-panel';
import { sortItemsByPage } from '../../../shared/utils/survey-rules/group-pages';

interface TabPanelProps { children: React.ReactNode; value: number; index: number; }
const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>{value === index && children}</Box>
);

/**
 * Standalone editor for a reusable question set (template). The survey hub hosts
 * the same panels for editing a set in the context of one survey.
 */
export const SurveyQuestionGroupDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const surveyAdminService = SurveyAdminService.getInstance();

  const [group, setGroup] = useState<ISurveyQuestionGroup>();
  // A set pushed from a partner's Library is theirs to maintain unless they have allowed
  // editing. The backend refuses every mutating path with a 403; these controls are greyed
  // rather than removed so the reason is visible instead of the buttons just vanishing.
  const isPartnerLocked = group?.canClientEdit === false;
  const [allQuestions, setAllQuestions] = useState<ISurveyQuestion[]>([]);
  const [questionTypes, setQuestionTypes] = useState<ISurveyQuestionType[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [openAddItem, setOpenAddItem] = useState(false);
  const [openCreateQuestion, setOpenCreateQuestion] = useState(false);
  const [editQuestion, setEditQuestion] = useState<ISurveyQuestion | null>(null);
  const [addItemPage, setAddItemPage] = useState(1);
  const [openEditGroup, setOpenEditGroup] = useState(false);

  const loadGroup = useCallback(async () => {
    if (!id) return;
    setGroup(undefined);
    try {
      const data = await surveyAdminService.getGroup(id);
      setGroup(data);
    } catch {
      setGroup(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadAllQuestions = useCallback(async () => {
    try {
      const result = await surveyAdminService.getQuestions({ active: true, perPage: 200 });
      setAllQuestions(result.rows);
    } catch {
      setAllQuestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadGroup(); loadAllQuestions(); }, [loadGroup, loadAllQuestions]);

  useEffect(() => {
    surveyAdminService.getQuestionTypes()
      .then(setQuestionTypes)
      .catch(() => setQuestionTypes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A question written here is added to this set the moment it is created.
  const handleQuestionCreated = async (savedQuestionId?: string) => {
    setOpenCreateQuestion(false);
    loadAllQuestions();
    if (!savedQuestionId || !id) { loadGroup(); return; }
    try {
      await surveyAdminService.addGroupItem(id, {
        surveyQuestionId: savedQuestionId,
        sortOrder: (group?.surveyQuestionGroupItems ?? []).length,
        pageNumber: addItemPage,
      });
    } catch {
      // The question exists in the library either way; the panel reload shows the truth.
    }
    loadGroup();
  };

  const items = group?.surveyQuestionGroupItems ?? [];
  const sortedItems = sortItemsByPage(items);
  const allRules = group?.surveyQuestionGroupEmissionRules ?? [];

  const existingQuestionIds = new Set(items.map(i => i.surveyQuestionId));
  const availableQuestions = allQuestions.filter(q => !existingQuestionIds.has(q.id));

  return (
    <PageOuterContainer>
      <LoadingPage pageReady={!!group}>
        <PageHeading
          title={group?.name ?? ''}
          includeBackButton
          backButtonText='Back to Question Sets'
          backButtonLink='/surveys/question-groups'
          includeActionButton
          actionButtonText='Edit Question Set'
          actionButtonIcon={IconName.EditOutlined}
          actionButtonHandler={() => setOpenEditGroup(true)}
          disableActionButton={isPartnerLocked}
          showChip={Boolean(group?.isPartnerManaged)}
          chipColor='default'
          chipLabel={partnerManagedTooltip({ partnerName: group?.partnerName, canClientEdit: group?.canClientEdit })}
        />

        {group?.description && (
          <Typography color='text.secondary' sx={{ mb: 2 }}>{group.description}</Typography>
        )}

        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 1 }}>
          <Tab label={`Questions (${items.length})`} />
          <Tab label={`Calculations (${allRules.length})`} />
        </Tabs>
        <Divider />

        <TabPanel value={tabValue} index={0}>
          <SurveyQuestionsPanel
            groupId={id!}
            sortedItems={sortedItems}
            locked={isPartnerLocked}
            onChanged={loadGroup}
            onAddQuestion={pageNum => { setAddItemPage(pageNum); setOpenAddItem(true); }}
            onCreateQuestion={pageNum => { setAddItemPage(pageNum); setOpenCreateQuestion(true); }}
            onEditQuestion={item => {
              const question = item.surveyQuestion ?? allQuestions.find(q => q.id === item.surveyQuestionId) ?? null;
              if (question) setEditQuestion(question);
            }}
            allQuestions={allQuestions}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <SurveyCalculationsPanel
            groupId={id!}
            groupItems={sortedItems}
            rules={allRules}
            locked={isPartnerLocked}
            onChanged={loadGroup}
          />
        </TabPanel>
      </LoadingPage>

      <AddGroupItemDialog
        open={openAddItem}
        onClose={() => setOpenAddItem(false)}
        successHandler={loadGroup}
        groupId={id!}
        availableQuestions={availableQuestions}
        nextSortOrder={items.length}
        targetPageNumber={addItemPage}
      />

      {group && (
        <SurveyQuestionGroupDialog
          open={openEditGroup}
          onClose={() => setOpenEditGroup(false)}
          successHandler={loadGroup}
          existingGroup={group}
        />
      )}

      {openCreateQuestion && (
        <SurveyQuestionDialog
          open={openCreateQuestion}
          onClose={() => setOpenCreateQuestion(false)}
          successHandler={handleQuestionCreated}
          questionTypes={questionTypes}
        />
      )}

      {editQuestion && (
        <SurveyQuestionDialog
          open={Boolean(editQuestion)}
          onClose={() => setEditQuestion(null)}
          successHandler={() => { setEditQuestion(null); loadAllQuestions(); loadGroup(); }}
          questionTypes={questionTypes}
          existingQuestion={editQuestion}
        />
      )}
    </PageOuterContainer>
  );
};
